import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import "../css/Login.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useRBAC } from "../../context/RBACContext";

export default function Login() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({ email: "", password: "", general: "" });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false); // loader state
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const navigate = useNavigate();
  const { initializeRBAC } = useRBAC();

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validate = () => {
    let valid = true;
    const newErrors = { email: "", password: "", general: "" };

    if (!formData.email) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address.";
      valid = false;
    }

    if (!formData.password) {
      newErrors.password = "Password is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));

    if (submitted) {
      const newErrors = { ...errors };
      if (name === "email") {
        if (!value) newErrors.email = "Email is required.";
        else if (!validateEmail(value)) newErrors.email = "Please enter a valid email address.";
        else newErrors.email = "";
      }
      if (name === "password") {
        if (!value) newErrors.password = "Password is required.";
        else newErrors.password = "";
      }
      setErrors(newErrors);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (e.target.name === "email" && !errors.email) passwordRef.current.focus();
      else if (e.target.name === "password") handleSubmit(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setErrors({ email: "", password: "", general: "" });

    // Validate form first
    if (!validate()) return;

    setLoading(true);

    try {
      // Sign in with AWS Cognito
      const user = await Auth.signIn(formData.email, formData.password);

      // Handle NEW_PASSWORD_REQUIRED challenge
      if (user.challengeName === "NEW_PASSWORD_REQUIRED") {
        navigate("/new-password", {
          state: { email: formData.email, tempPassword: formData.password },
        });
        return;
      }

      // Initialize RBAC (load role & permissions into context/session)
      await initializeRBAC();

      // Get current authenticated user attributes (for navigation)
      const userInfo = await Auth.currentAuthenticatedUser();
      const roleAttr = userInfo.attributes["custom:role"]?.trim() || "";
      const roleLower = roleAttr.toLowerCase();

      // Role-based navigation
      if (
        roleLower === "buyer" ||
        roleLower === "procurement" ||
        roleLower === "finance" ||
        roleLower === "collaborator" ||
        roleLower === "technical"
      ) {
        navigate("/buyer-dashboard");
      } else if (roleLower === "seller" || roleLower === "seller team") {
        navigate("/seller-dashboard");
      }
      else if (roleLower === "project manager") {
        navigate("/manager-dashboard");
      } else if (roleLower === "admin" || roleLower === "super admin") {
        console.log("Admin user, navigating to admin dashboard");
        navigate("/admin-dashboard");
      } else {
        setErrors((prev) => ({
          ...prev,
          general: "User role is not set. Contact admin.",
        }));
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrors((prev) => ({
        ...prev,
        general: error.message || "Failed to sign in",
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="d-flex flex-column justify-content-between align-items-center px-4 py-5 position-relative w-100 h-100"
      style={{ maxWidth: "400px", margin: "0 auto" }}
    >
      <div className="w-100 d-flex flex-column align-items-center">
        <div className="mb-4 mt-5 text-center">
          <img src="/images/Logo.png" alt="Logo" style={{ height: "60px" }} />
        </div>

        <h3 className="fw-bold text-center">Log in to your account</h3>
        <p className="text-center text-muted mb-4" style={{ fontSize: "14px" }}>
          Enter your details to proceed further
        </p>

        <form onSubmit={handleSubmit} className="w-100">
          {/* Email */}
          <div className="mb-3">
            <label
              htmlFor="email"
              className={`form-label fw-semibold mb-2 ${errors.email ? "text-danger" : ""}`}
            >
              Your email address
            </label>
            <div className="input-icon-wrapper">
              <input
                type="email"
                name="email"
                ref={emailRef}
                disabled={loading}
                className={`form-control border-2 custom-focus ${errors.email ? "input-error" : ""}`}
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
              />
              {errors.email && submitted && (
                <img src="/images/failure.svg" alt="Error" className="input-error-icon" />
              )}
            </div>
            {submitted && touched.email && errors.email && (
              <div className="alert-box">
                <img src="/images/security.svg" alt="Info" width={20} height={20} />
                <div className="flex-grow-1">
                  <div className="fw-bold text-dark">Error</div>
                  <div>{errors.email}</div>
                </div>
                <span
                  className="close-icon"
                  onClick={() => setErrors((prev) => ({ ...prev, email: "" }))}
                >
                  ❌
                </span>
              </div>
            )}
          </div>

          {/* Password */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <label
                htmlFor="password"
                className={`form-label fw-semibold mb-2 ${errors.password ? "text-danger" : ""}`}
              >
                Enter password
              </label>
              <Link to="/forget-password" className="small text-muted">
                Forgot password?
              </Link>
            </div>
            <div className="input-icon-wrapper position-relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                ref={passwordRef}
                disabled={loading}
                className={`form-control border-2 custom-focus ${errors.password ? "input-error" : ""}`}
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
              />
              <span
                className="position-absolute end-0 top-50 translate-middle-y me-2"
                style={{ cursor: "pointer" }}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
              </span>
            </div>
            {submitted && touched.password && errors.password && (
              <div className="alert-box">
                <img src="/images/security.svg" alt="Info" width={20} height={20} />
                <div className="flex-grow-1">
                  <div className="fw-bold text-dark">Error</div>
                  <div>{errors.password}</div>
                </div>
                <span
                  className="close-icon"
                  onClick={() => setErrors((prev) => ({ ...prev, password: "" }))}
                >
                  ❌
                </span>
              </div>
            )}
          </div>

          {/* General error */}
          {errors.general && (
            <div className="alert-box">
              <img src="/images/security.svg" alt="Info" width={20} height={20} />
              <div className="flex-grow-1">
                <div className="fw-bold text-dark">Error</div>
                <div>{errors.general}</div>
              </div>
              <span
                className="close-icon"
                onClick={() => setErrors((prev) => ({ ...prev, general: "" }))}
              >
                ❌
              </span>
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary btn-md w-100 mt-3"
            disabled={loading}
            style={{ backgroundColor: "#181D4C", height: "40px" }}
          >
            {loading ? "Logging in..." : "Log in"}
          </button>

          <p className="text-muted text-center small mt-4">
            By proceeding, you agree to the <br />
            <span className="text-dark text-decoration-underline">Terms of Privacy</span> and{" "}
            <span className="text-dark text-decoration-underline">Policy</span>.
          </p>
        </form>
      </div>

      <p className="w-100 text-center mb-3">
        <span className="text-muted">Don’t have an account? </span>
        <Link to="/onboarding" className="text-dark text-decoration-underline">
          Sign up
        </Link>
      </p>
    </div>
  );
}
